import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { getInvoicingConfig, updateInvoicingConfig, uploadInvoicingCertificate } from '../lib/api';
import { toast } from 'sonner';
import { FileKey, Loader2, Receipt } from 'lucide-react';

const FACTURAPI_KEY_MASK = '********';

const emptyForm = {
  legal_name: '',
  tax_id: '',
  tax_system: '612',
  zip: '',
  series: 'F',
  default_product_key: '85121500',
  default_unit_key: 'E48',
  facturapi_organization_id: 'me',
  facturapi_secret_key: '',
};

export default function InvoicingSettingsCard() {
  const [form, setForm] = useState(emptyForm);
  const [meta, setMeta] = useState({
    facturapi_configured: false,
    facturapi_mode: 'unset',
    csd_has_certificate: false,
    csd_expires_at: '',
    csd_serial_number: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingCsd, setUploadingCsd] = useState(false);
  const [cerFile, setCerFile] = useState(null);
  const [keyFile, setKeyFile] = useState(null);
  const [csdPassword, setCsdPassword] = useState('');

  const applyConfig = (cfg) => {
    setForm({
      legal_name: cfg.legal_name || '',
      tax_id: cfg.tax_id || '',
      tax_system: cfg.tax_system || '612',
      zip: cfg.zip || '',
      series: cfg.series || 'F',
      default_product_key: cfg.default_product_key || '85121500',
      default_unit_key: cfg.default_unit_key || 'E48',
      facturapi_organization_id: cfg.facturapi_organization_id || 'me',
      facturapi_secret_key: cfg.facturapi_secret_key || '',
    });
    setMeta({
      facturapi_configured: Boolean(cfg.facturapi_configured),
      facturapi_mode: cfg.facturapi_mode || 'unset',
      csd_has_certificate: Boolean(cfg.csd_has_certificate),
      csd_expires_at: cfg.csd_expires_at || '',
      csd_serial_number: cfg.csd_serial_number || '',
    });
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const cfg = await getInvoicingConfig();
        if (alive) applyConfig(cfg);
      } catch {
        if (alive) toast.error('No se pudo cargar la configuración de facturación');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const handleSaveLegal = async () => {
    setSaving(true);
    try {
      const saved = await updateInvoicingConfig({
        ...form,
        tax_id: form.tax_id.trim().toUpperCase(),
        facturapi_secret_key: form.facturapi_secret_key || FACTURAPI_KEY_MASK,
      });
      applyConfig(saved);
      toast.success(
        saved.facturapi_configured
          ? 'Configuración guardada y sincronizada con Facturapi'
          : 'Datos guardados (agregue la Secret Key para sincronizar)',
      );
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadCsd = async () => {
    if (!cerFile || !keyFile || !csdPassword.trim()) {
      toast.error('Seleccione .cer, .key e indique la contraseña');
      return;
    }
    setUploadingCsd(true);
    try {
      const saved = await uploadInvoicingCertificate({
        cerFile,
        keyFile,
        password: csdPassword,
      });
      applyConfig(saved);
      setCerFile(null);
      setKeyFile(null);
      setCsdPassword('');
      toast.success('CSD cargado en Facturapi');
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'No se pudo subir el CSD');
    } finally {
      setUploadingCsd(false);
    }
  };

  if (loading) {
    return (
      <Card className="border border-border">
        <CardContent className="py-8 text-sm text-muted-foreground">Cargando facturación…</CardContent>
      </Card>
    );
  }

  return (
    <Card className="border border-border">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Receipt className="w-5 h-5 text-primary" />
          Facturación electrónica — emisor
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm text-muted-foreground">
          Secret Key de Facturapi, datos fiscales del negocio y CSD (.cer / .key) para timbrar CFDI. En live también
          debe firmarse la Carta Manifiesto en Facturapi con FIEL.
        </p>
        <p className="text-xs text-muted-foreground">
          Facturapi:{' '}
          <strong>{meta.facturapi_configured ? meta.facturapi_mode : 'sin Secret Key'}</strong>
          {meta.csd_has_certificate
            ? ` · CSD activo${meta.csd_expires_at ? ` (vence ${String(meta.csd_expires_at).slice(0, 10)})` : ''}`
            : ' · CSD pendiente'}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label htmlFor="facturapi-secret-key">Secret Key de Facturapi</Label>
            <Input
              id="facturapi-secret-key"
              type="password"
              className="mt-1 h-11 font-mono text-sm"
              value={form.facturapi_secret_key}
              onChange={(e) => setForm({ ...form, facturapi_secret_key: e.target.value })}
              placeholder="sk_test_… o sk_live_…"
              autoComplete="new-password"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Se guarda en la base de datos (no en .env). Si ve {FACTURAPI_KEY_MASK}, ya hay una key guardada; pegue
              una nueva solo para reemplazarla.
            </p>
          </div>
          <div className="md:col-span-2">
            <Label>Razón social / nombre fiscal</Label>
            <Input
              className="mt-1 h-11"
              value={form.legal_name}
              onChange={(e) => setForm({ ...form, legal_name: e.target.value })}
              placeholder="Sin S.A. de C.V. / similares"
            />
          </div>
          <div>
            <Label>RFC</Label>
            <Input
              className="mt-1 h-11 uppercase"
              maxLength={13}
              value={form.tax_id}
              onChange={(e) => setForm({ ...form, tax_id: e.target.value })}
            />
          </div>
          <div>
            <Label>Régimen fiscal</Label>
            <Input
              className="mt-1 h-11"
              maxLength={3}
              value={form.tax_system}
              onChange={(e) => setForm({ ...form, tax_system: e.target.value })}
              placeholder="612"
            />
          </div>
          <div>
            <Label>Código postal fiscal</Label>
            <Input
              className="mt-1 h-11"
              maxLength={5}
              value={form.zip}
              onChange={(e) => setForm({ ...form, zip: e.target.value })}
            />
          </div>
          <div>
            <Label>Serie de facturas</Label>
            <Input
              className="mt-1 h-11"
              value={form.series}
              onChange={(e) => setForm({ ...form, series: e.target.value })}
            />
          </div>
          <div>
            <Label>Clave SAT producto (default)</Label>
            <Input
              className="mt-1 h-11"
              value={form.default_product_key}
              onChange={(e) => setForm({ ...form, default_product_key: e.target.value })}
            />
          </div>
          <div>
            <Label>Clave unidad SAT (default)</Label>
            <Input
              className="mt-1 h-11"
              value={form.default_unit_key}
              onChange={(e) => setForm({ ...form, default_unit_key: e.target.value })}
            />
          </div>
        </div>

        <Button type="button" onClick={handleSaveLegal} disabled={saving} className="rounded-full gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {saving ? 'Guardando…' : 'Guardar datos fiscales'}
        </Button>

        <div className="rounded-lg border border-dashed p-4 space-y-3">
          <p className="text-sm font-semibold flex items-center gap-2">
            <FileKey className="w-4 h-4" />
            Certificado de sello digital (CSD)
          </p>
          <p className="text-xs text-muted-foreground">
            Suba los archivos del SAT. La contraseña solo se usa para enviarla a Facturapi; no se guarda en Centro Cultural.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label htmlFor="csd-cer">Archivo .cer</Label>
              <Input
                id="csd-cer"
                type="file"
                accept=".cer,.CER"
                className="mt-1 cursor-pointer"
                onChange={(e) => setCerFile(e.target.files?.[0] || null)}
              />
            </div>
            <div>
              <Label htmlFor="csd-key">Archivo .key</Label>
              <Input
                id="csd-key"
                type="file"
                accept=".key,.KEY"
                className="mt-1 cursor-pointer"
                onChange={(e) => setKeyFile(e.target.files?.[0] || null)}
              />
            </div>
            <div>
              <Label htmlFor="csd-password">Contraseña del .key</Label>
              <Input
                id="csd-password"
                type="password"
                className="mt-1 h-11"
                value={csdPassword}
                onChange={(e) => setCsdPassword(e.target.value)}
                autoComplete="new-password"
              />
            </div>
          </div>
          <Button
            type="button"
            variant="secondary"
            disabled={uploadingCsd}
            onClick={handleUploadCsd}
            className="rounded-full gap-2"
          >
            {uploadingCsd ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {uploadingCsd ? 'Subiendo…' : 'Subir CSD a Facturapi'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
